import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiLayers, FiClock, FiEdit3, FiDownload, FiCheck } = FiIcons;

const Navigation = ({ completedSections = [], authorName = 'Author' }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const tabs = [
    { 
      id: 'bookVision', 
      label: 'Book Vision', 
      icon: FiTarget, 
      path: '/book-vision',
      description: 'Define your purpose & calling'
    },
    { 
      id: 'structure', 
      label: 'Structure & Outline', 
      icon: FiLayers, 
      path: '/structure',
      description: 'Build your book framework'
    },
    { 
      id: 'scope', 
      label: 'Project Scope', 
      icon: FiClock, 
      path: '/scope',
      description: 'Plan timeline & goals'
    },
    { 
      id: 'kickstart', 
      label: 'Chapter Kickstart', 
      icon: FiEdit3, 
      path: '/kickstart',
      description: 'Write your first words'
    },
    { 
      id: 'summary', 
      label: 'Summary & Export', 
      icon: FiDownload, 
      path: '/summary',
      description: 'Review & download plan'
    }
  ];

  const handleTabClick = (tab) => {
    navigate(tab.path);
  };

  const isCompleted = (tabId) => {
    return completedSections.includes(tabId);
  };

  const currentPath = location.pathname;
  const completedCount = completedSections.length;
  const totalSections = tabs.length;
  const progressPercentage = (completedCount / totalSections) * 100;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-stone-200">
      <div className="max-w-4xl mx-auto px-4 py-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold bg-gradient-to-r from-brand-orange to-brand-blue bg-clip-text text-transparent">
              Planning with Purpose
            </h1>
            <p className="text-sm text-stone-600">Welcome back, {authorName}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium text-brand-blue">
              {completedCount} of {totalSections} Complete
            </p>
            <div className="w-20 h-2 bg-stone-200 rounded-full mt-1">
              <motion.div
                className="h-full bg-gradient-to-r from-brand-orange to-brand-blue rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <nav className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 border border-stone-200 shadow-sm">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab, index) => {
              const isActive = currentPath === tab.path;
              const isTabCompleted = isCompleted(tab.id);
              
              return (
                <motion.button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={`
                    flex-1 min-w-0 p-4 rounded-xl transition-all duration-200 relative
                    ${isActive 
                      ? 'bg-gradient-to-r from-brand-orange to-brand-blue text-white shadow-lg' 
                      : 'bg-transparent text-stone-600 hover:bg-stone-100'
                    }
                  `}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex flex-col items-center text-center">
                    <div className="flex items-center justify-center mb-2 relative">
                      <SafeIcon 
                        icon={tab.icon} 
                        className={`text-xl ${isActive ? 'text-white' : 'text-stone-500'}`} 
                      />
                      {isTabCompleted && !isActive && (
                        <SafeIcon 
                          icon={FiCheck} 
                          className="absolute -top-1 -right-1 text-emerald-500 text-sm bg-white rounded-full p-0.5" 
                        />
                      )}
                    </div>
                    <h3 className={`font-medium text-sm mb-1 ${isActive ? 'text-white' : 'text-stone-700'}`}>
                      {tab.label}
                    </h3>
                    <p className={`text-xs hidden sm:block ${isActive ? 'text-white/80' : 'text-stone-500'}`}>
                      {tab.description}
                    </p>
                  </div>
                </motion.button>
              );
            })}
          </div>
          
          {/* Motivational Message */}
          <div className="mt-4 px-2">
            <p className="text-xs text-stone-500 text-center">
              {completedCount === 0 && "Begin your journey of faithful stewardship"}
              {completedCount > 0 && completedCount < totalSections && "You're making progress! Keep stewarding your gift well."}
              {completedCount === totalSections && "Congratulations! Your book plan is complete. Time to write!"}
            </p>
          </div>
        </nav>
      </div>
    </div>
  );
};

export default Navigation;